import React, { useEffect } from "react";
import { useLicense } from "../hooks/useLicense";
import { useNavigate } from "react-router-dom";

/**
 * Wraps routes that require a specific product.
 *
 * Props:
 *   product: "octopush_x" | "recording_studio"
 *   crossAppUrl: URL of the sibling app to redirect to when the user has
 *                the *other* product (optional — if omitted, shows "no access" screen)
 *
 * Behaviour:
 *   - loading → renders spinner/children? (we render a lightweight loading)
 *   - user has access → renders children
 *   - user has the OTHER product only → redirects to crossAppUrl (if provided)
 *   - user has neither → shows a "no access" screen
 */
const ProductGuard = ({ product, crossAppUrl, children }) => {
  const { loading, hasOctopushX, hasRecordingStudio, accessType } =
    useLicense();
  const navigate = useNavigate();

  const hasThisProduct =
    product === "octopush_x" ? hasOctopushX : hasRecordingStudio;
  const hasOtherProduct =
    product === "octopush_x" ? hasRecordingStudio : hasOctopushX;

  useEffect(() => {
    if (loading) return;
    if (hasThisProduct) return;
    if (hasOtherProduct && crossAppUrl) {
      window.location.href = crossAppUrl;
    }
  }, [loading, hasThisProduct, hasOtherProduct, crossAppUrl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] flex items-center justify-center">
        <div className="text-gray-400 text-sm">Checking access…</div>
      </div>
    );
  }

  if (hasThisProduct) return children;

  // If we’re about to redirect to the sibling app, show a brief message
  if (hasOtherProduct && crossAppUrl) {
    return (
      <div className="min-h-screen bg-[#0C0E12] flex items-center justify-center">
        <div className="text-gray-400 text-sm">
          Redirecting to your workspace…
        </div>
      </div>
    );
  }

  // No access anywhere
  return (
    <div className="min-h-screen bg-[#0C0E12] flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-semibold text-white mb-3">
          No access to this product
        </h1>
        <p className="text-gray-400 text-sm mb-6">
          Your account doesn't have an active license for{" "}
          <span className="text-white font-medium">
            {product === "octopush_x" ? "Octopush X" : "the Recording Studio"}
          </span>
          . Contact your administrator if you think this is a mistake.
        </p>
        <p className="text-xs text-gray-500">
          Current access:{" "}
          <span className="uppercase">{accessType || "none"}</span>
        </p>
        <button
          onClick={() => {
            // Trigger logout
            navigate("/login?reason=no_access");
          }}
          className="mt-6 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm"
        >
          Back to login
        </button>
      </div>
    </div>
  );
};

export default ProductGuard;
